/**
 * Child-segment loader for the paginated `geosoft.object-index/1` profile.
 *
 * A paginated index ships a tiny root shard plus many child segment files
 * (`geosoft.object-index-segment/1`). When the operator expands a *boundary*
 * node — one carrying `metadata.hierarchyUrl` pointing at a `.json` segment —
 * the Scene Tree fetches that segment and merges its objects into the live
 * manifest. The segment's objects are the descendants of the boundary node,
 * already linked by `parentId`, so no re-parenting is required.
 *
 * Never throws: returns `null` on any failure (logs only in DEV), mirroring
 * fetchObjectIndexManifest.
 */

import { normalizeObjectIndexRecord } from "./objectIndexToManifest";
import type {
  ObjectIndexRecord,
  UniversalSceneNode,
} from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";

interface ObjectIndexSegment {
  schema?: string;
  parentId?: string | number | null;
  objects?: ObjectIndexRecord[];
}

function devError(message: string, err?: unknown): void {
  if (import.meta.env.DEV) {
    console.error(`[scene-hierarchy:segment] ${message}`, err ?? "");
  }
}

/**
 * Resolve a node's (possibly relative) segment URL against the manifest's
 * `segmentBaseUrl`. Absolute URLs are returned unchanged.
 */
export function resolveSegmentUrl(
  segUrl: string,
  segmentBaseUrl: string | undefined,
): string {
  if (/^https?:\/\//i.test(segUrl) || segUrl.startsWith("/")) return segUrl;
  if (!segmentBaseUrl) return segUrl;
  try {
    // Anchor relative resolution to the document origin for app-relative bases.
    const anchor = /^https?:\/\//i.test(segmentBaseUrl)
      ? segmentBaseUrl
      : new URL(segmentBaseUrl, window.location.origin).href;
    return new URL(segUrl, anchor).href;
  } catch {
    return segmentBaseUrl + segUrl;
  }
}

/**
 * Fetch and normalise one child segment into UniversalSceneNodes.
 *
 * @param url       - Fully-resolved segment URL.
 * @param fetchImpl - Injectable fetch (defaults to global `fetch`).
 * @returns The segment's nodes, or `null` on fetch/parse failure.
 */
export async function fetchObjectIndexSegment(
  url: string,
  fetchImpl: typeof fetch = fetch,
): Promise<UniversalSceneNode[] | null> {
  if (!url) return null;
  let seg: ObjectIndexSegment;
  try {
    const response = await fetchImpl(url);
    if (!response.ok) {
      devError(`segment fetch failed for "${url}" — HTTP ${response.status}`);
      return null;
    }
    seg = (await response.json()) as ObjectIndexSegment;
  } catch (err) {
    devError(`segment fetch/parse failed for "${url}"`, err);
    return null;
  }
  if (!Array.isArray(seg.objects)) {
    devError(`segment "${url}" has no objects[]`);
    return null;
  }
  return seg.objects.map(normalizeObjectIndexRecord);
}
